import React, { useEffect, useState } from "react";
import { getAnalytics } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

export default function AnalyticsPanel({ refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    getAnalytics().then(setData).catch(() => {});
  }, [refreshKey]);

  if (!data) return <div className="rc-panel p-4 h-full"><div className="rc-label rc-blink">LOADING ANALYTICS</div></div>;

  return (
    <div className="rc-panel p-4 h-full flex flex-col" data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="rc-label">// APP ANALYTICS</div>
        <div className="rc-label text-[9px]">LIVE</div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Stat label="VIEWS / 24H" value={data.totals.views_today.toLocaleString()} testid="stat-views" />
        <Stat label="SUBSCRIBERS" value={data.totals.subscribers.toLocaleString()} testid="stat-subs" />
        <Stat label="REVENUE / 24H" value={`£${data.totals.revenue_today.toLocaleString()}`} testid="stat-rev-today" />
        <Stat label="REVENUE / TOTAL" value={`£${data.totals.revenue_total.toLocaleString()}`} testid="stat-rev-total" />
      </div>

      <div className="h-28 mb-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.trend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fill: "#a88554", fontSize: 9 }} axisLine={{ stroke: "#ffb80044" }} tickLine={false} />
            <YAxis hide />
            <Tooltip contentStyle={{ background: "#0a0703", border: "1px solid #ffb80044", fontSize: 11 }} labelStyle={{ color: "#ffb800" }} />
            <Line type="monotone" dataKey="views" stroke="#ffb800" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2">
        {data.apps.map(a => {
          const m = a.metrics || {};
          const primary = m.daily_active ?? m.products ?? m.strategies_live ?? m.jobs_queued ?? m.uptime_pct ?? "—";
          const secondary = m.messages_today ?? m.stock_alerts ?? m.open_positions ?? m.gpu_util ?? "";
          return (
            <div key={a.id || a.key} className="border border-[var(--rc-gold-dim)] p-2 hover:border-[var(--rc-gold)] transition-colors" data-testid={`app-row-${a.key}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-xs tracking-widest truncate">{a.name}</span>
                <span className="text-xs rc-glow-text tabular-nums">{typeof primary === "number" ? primary.toLocaleString() : primary}</span>
              </div>
              <div className="rc-label text-[9px] mt-1 truncate">{a.category.toUpperCase()} · {secondary !== "" ? (typeof secondary === "number" ? secondary.toLocaleString() : secondary) : "ONLINE"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-[var(--rc-gold-dim)] p-2" data-testid={testid}>
      <div className="rc-label text-[9px]">{label}</div>
      <div className="font-display text-base rc-glow-text mt-1 tabular-nums truncate">{value}</div>
    </div>
  );
}
